import React from "react";
import { isDraftNumber } from "../../utils/invoiceNumber";

/**
 * The printed invoice, exactly as the firm's own file copies look.
 *
 * Both the Create preview and the Invoice detail page render this. They used
 * to carry their own copies of the layout, which is why a fix to the dollar
 * sign could land on one page and not the other.
 *
 * The money column is the whole geometry of the page. Every row -- a fee, the
 * discount, the taxes, the total -- is one MoneyRow of a fixed width, pushed
 * to the right margin. The date and invoice number sit in a block of that
 * same width and margin, so they share a left edge with the word "Fee". The
 * dollar sign lives *inside* the amount cell, pinned to its left edge, which
 * is where the rules start: $ and underline share an edge.
 *
 * The page is a flex COLUMN one sheet tall, with a flexible spacer between the
 * totals and the firm's own block. That is what makes the GST registration sit
 * just above the footer and the footer hug the foot of the sheet, on a short
 * invoice and a long one alike -- the old layout let both float up the page
 * behind whatever content happened to end where it did.
 */

// The gap between the letterhead and the date. The firm's own invoices leave a
// deep one -- the date sits about 30% down the sheet -- and without it the date
// and invoice number crowd the logo.
//
// It SHRINKS before the page does. A seven-bullet invoice (Raider Transport)
// fills the sheet, and compressing this gap is a far better way to fit than
// spilling a second page carrying nothing but the footer.
const DESIGN_FONT_SIZE = 13;
const LETTERHEAD_GAP = 150;
const LETTERHEAD_GAP_MIN = 0;
// The breathing room the firm's block prefers above it. A PREFERRED size, not
// a floor: a floor holds room the content needs and pushes the longest invoice
// onto a second page. As a flex basis it instead competes with the letterhead
// gap for whatever slack the sheet has, so a long invoice moves UP under the
// logo rather than spilling -- which is how the firm sets its own long
// invoices (a three-bullet one starts a third down the sheet, a seven-bullet
// one at about a fifth). 150 against 100 makes the gap yield the larger share.
const FOOT_BREATHING_ROOM = 100;

// The rest of the vertical rhythm. These are not taste: they are the LOOSEST
// values at which the firm's longest invoice (Raider Transport, seven bullets
// and four fees) still prints on a single sheet. Tightening the TOTALS block
// below is what bought the bullets their comfortable 14px back -- loosen the
// margins around them again and Raider spills even at that spacing. Verified by rendering to PDF
// in Chrome and counting pages -- measuring the box on screen is not enough,
// because the print page box is a few pixels shorter than the viewport and an
// invoice that "fits" on screen can still spill.
//
// Loosen them and Raider gains a second page carrying nothing but the footer.
// They cost a SHORT invoice nothing: the spacer below the totals absorbs the
// difference, so the date stays deep below the letterhead and the footer stays
// on the foot of the page either way.
const BULLET_GAP = 14;
const ADDRESS_GAP = 18;
const INTRO_GAP = 14;
const LINES_GAP = 10;
const GST_GAP = 18;

// The totals stack. On the firm's invoices Disbursements, PST and GST sit
// about 18px apart -- noticeably tighter than body text, which at 13px/1.5
// leaves them 28px apart and looking airy next to the paper.
const TOTALS_LINE_HEIGHT = 1.35;
// The breath before a new group of figures (the taxes, the total, the balance
// rows). Between rows WITHIN a group there is none.
const TOTALS_GROUP_GAP = 12;
// A little air between the last fee line (and the rule under it) and the
// first figure of the totals -- the subtotal, or the discount when there is
// one. Without it the subtotal sat hard against the rule.
const SUBTOTAL_GAP = 6;

/** Every spacing above is quoted at 13pt; scale it with the firm's size.
 *
 * Fixed px spacing under shrinking type gives a cramped page with acres of
 * white between the blocks -- the proportions are what make it read as the
 * firm's stationery, not the absolute sizes.
 */
function scaled(fontSize) {
  const size = Number(fontSize) > 0 ? Number(fontSize) : DESIGN_FONT_SIZE;
  const k = size / DESIGN_FONT_SIZE;
  const px = (v) => Math.round(v * k);
  return {
    fontSize: size,
    letterheadGap: px(LETTERHEAD_GAP),
    bulletGap: px(BULLET_GAP),
    addressGap: px(ADDRESS_GAP),
    introGap: px(INTRO_GAP),
    linesGap: px(LINES_GAP),
    gstGap: px(GST_GAP),
    totalsGroupGap: px(TOTALS_GROUP_GAP),
    subtotalGap: px(SUBTOTAL_GAP),
    moneyWidth: px(MONEY_WIDTH),
    amountWidth: px(AMOUNT_WIDTH),
  };
}

const MONEY_WIDTH = 250;      // label + amount cell
const AMOUNT_WIDTH = 110;     // the cell the rules underline
const RULE = "1px solid #000";
const DOUBLE_RULE = "3px double #000";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** "2026-07-04" -> "July 4, 2026", without letting a timezone move the day. */
export function formatInvoiceDate(value) {
  if (!value) return "";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(value);
  const [, year, month, day] = match;
  return `${MONTHS[Number(month) - 1]} ${Number(day)}, ${year}`;
}

/** The care-of name, with exactly one "c/o" on the front.
 *
 * 64 of the firm's 107 invoices carrying a care-of already have the prefix
 * inside the stored value, from the historical import -- so prepending one
 * unconditionally addressed the client "c/o c/o Peter J.D. Tyerman". The slash
 * is required, so a name like "Colin" is left alone.
 *
 * Returns "" when there is nothing but the prefix.
 */
export function careOfLine(value) {
  const name = String(value || "")
    .replace(/^\s*c\s*\/\s*o\b\.?\s*/i, "")
    .trim();
  return name ? `c/o ${name}` : "";
}

const num = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value) =>
  num(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** Accountants bracket a negative rather than signing it. */
const bracketed = (value) => `(${money(Math.abs(num(value)))})`;

function MoneyRow({
  name,
  label = "",
  currency = "",
  amount,
  rule = "none",
  bold = false,
  style = {},
  moneyWidth = MONEY_WIDTH,
  amountWidth = AMOUNT_WIDTH,
}) {
  return (
    <div
      data-money-row=""
      data-testid={`money-row-${name}`}
      style={{
        width: `${moneyWidth}px`,
        marginLeft: "auto",
        display: "flex",
        alignItems: "flex-end",
        fontWeight: bold ? 700 : "normal",
        ...style,
      }}
    >
      <span data-testid="money-label" style={{ flex: 1, minWidth: 0 }}>
        {label}
      </span>
      <span
        data-testid="money-amount-cell"
        style={{
          width: `${amountWidth}px`,
          flexShrink: 0,
          position: "relative",
          textAlign: "right",
          borderBottom: rule,
          paddingBottom: rule === "none" ? 0 : 2,
        }}
      >
        {/* Pinned to the left edge of the cell the rule underlines, so the
            dollar sign and the start of the underline line up. */}
        <span
          data-testid="money-currency"
          style={{ position: "absolute", left: 0, bottom: rule === "none" ? 0 : 2 }}
        >
          {currency}
        </span>
        <span data-testid="money-amount">{amount}</span>
      </span>
    </div>
  );
}

export default function InvoiceDocument({ invoice, client, children, fontSize }) {
  const inv = invoice || {};
  const cli = client || {};
  const S = scaled(fontSize);
  // Every MoneyRow on the page shares the scaled column widths.
  const money$ = { moneyWidth: S.moneyWidth, amountWidth: S.amountWidth };

  const lines = Array.isArray(inv.lines) ? inv.lines : [];
  const feeLines = lines.filter((line) => num(line.amount) > 0);
  const firstFeeIndex = lines.findIndex((line) => num(line.amount) > 0);

  const discount = num(inv.discount);
  const hasDiscount = discount > 0;
  const deposits = num(inv.deposits_received);
  const previousBalance = num(inv.previous_balance);
  // Only ever non-zero on the 2026 invoices loaded from paper. A ticked
  // exemption needs no figure: its row is gone, so there is nothing to star.
  const pstExempt = num(inv.pst_exempt_amount);

  const taxMode = inv.tax_mode_ui || inv.tax_mode || "GST_PST";
  const pstAmount = num(inv.tax_pst != null ? inv.tax_pst : inv.pst);
  const gstAmount = num(inv.tax_gst != null ? inv.tax_gst : inv.gst);
  const hstAmount = num(inv.tax_hst != null ? inv.tax_hst : inv.hst);
  // A tax row earns its place by carrying a figure. The exception is a PST
  // exemption, which prints a dash on purpose so the client can see the tax
  // was considered and waived rather than forgotten.
  // A tax that was not charged does not get a row. The firm starred an exempt
  // PST on paper and footnoted it; they asked for the line gone instead, so
  // one rule covers every exemption.
  //
  // A PARTIAL exemption is the exception and keeps both row and footnote: tax
  // really was charged on the remainder, and dropping the row would
  // under-state what the client was billed.
  const showPst = taxMode === "GST_PST" && pstAmount > 0;
  const showGst = (taxMode === "GST" || taxMode === "GST_PST") && gstAmount > 0;
  const showHst = taxMode === "HST" && hstAmount > 0;

  const total = num(inv.total_amount != null ? inv.total_amount : inv.total);
  const balanceDue = total - deposits + previousBalance;

  // The rule above the subtotal sits under whichever figure is last before it.
  const ruleOnLastFee = !hasDiscount;
  const lastFeeLineIndex = feeLines.length
    ? lines.lastIndexOf(feeLines[feeLines.length - 1])
    : -1;

  const street = cli.street_address || "";
  const city = cli.city || "";
  const province = cli.province || "";
  const postal = cli.postal_code || "";
  const hasAddress = Boolean(street || city || province || postal);

  const billTo = inv.bill_to || cli.bill_to || cli.name || "";

  return (
    <div
      className="inv-doc"
      data-testid="invoice-document"
      style={{ position: "relative", fontSize: S.fontSize }}
    >
      {/* Owned here rather than by either page, so the printed sheet looks the
          same whichever screen it was printed from. */}
      <style>{`
        .inv-doc {
          display: flex;
          flex-direction: column;
          flex: 1 1 auto;
          min-height: 0;
        }
        @media print {
          /* Beats a page's own blanket "display: block" print rule, which
             would otherwise flatten every money row and lose the columns. */
          .inv-doc { display: flex !important; }
          .inv-doc-foot { break-inside: avoid; page-break-inside: avoid; }
          /* Prompts for whoever is writing the invoice, never lines on the
             invoice. One of these printed on 27-027 and the client would have
             received "No address on file for this client." */
          .inv-doc .screen-only { display: none !important; }
        }
      `}</style>

      {children}

      <div style={{ textAlign: "center" }}>
        <img
          src="/lh-logo.png"
          alt="Lee Humble CPA Logo"
          style={{ width: 200, height: "auto", margin: "0 auto 15px auto", display: "block" }}
        />
      </div>

      <div
        data-testid="doc-letterhead-gap"
        style={{
          flex: `0 1 ${S.letterheadGap}px`,
          minHeight: LETTERHEAD_GAP_MIN,
        }}
      />

      {/* Date and number, in the money column so they align with "Fee". */}
      <div
        data-testid="doc-meta"
        style={{ width: `${S.moneyWidth}px`, marginLeft: "auto", marginBottom: S.totalsGroupGap * 2.5 }}
      >
        <div data-testid="doc-date">{formatInvoiceDate(inv.date)}</div>
        {inv.number ? (
          <div data-testid="doc-number">
            {/* A draft has no number yet: it prints as a draft. */}
            {isDraftNumber(inv.number) ? "DRAFT" : `Invoice #${inv.number}`}
          </div>
        ) : null}
        {inv.due_date ? (
          <div data-testid="doc-due-date">Due {formatInvoiceDate(inv.due_date)}</div>
        ) : null}
      </div>

      <div data-testid="doc-address" style={{ marginBottom: S.addressGap, lineHeight: 1.45 }}>
        <div>{billTo}</div>
        {inv.operating_as ? <div>o/a {inv.operating_as}</div> : null}
        {careOfLine(inv.care_of) ? (
          <div data-testid="doc-care-of">{careOfLine(inv.care_of)}</div>
        ) : null}
        {hasAddress ? (
          <>
            {street ? <div>{street}</div> : null}
            {city || province || postal ? (
              <div data-testid="doc-city-line">
                {[city, province].filter(Boolean).join(", ")}
                {postal ? `  ${postal}` : ""}
              </div>
            ) : null}
          </>
        ) : (
          <div
            className="screen-only"
            data-testid="doc-address-missing"
            style={{ color: "#b45309", fontStyle: "italic" }}
          >
            No address on file for this client.
          </div>
        )}
      </div>

      {inv.intro_line ? (
        <div data-testid="doc-intro" style={{ marginBottom: S.introGap }}>
          {inv.intro_line}
        </div>
      ) : null}

      <div style={{ marginBottom: S.linesGap }}>
        {lines.map((line, index) => {
          const amount = num(line.amount);
          const hasAmount = amount > 0;
          return (
            <div
              key={index}
              data-line-row=""
              style={{
                display: "flex",
                alignItems: "flex-end",
                marginBottom: S.bulletGap,
              }}
            >
              {/* The bullet and its text share ONE half-width block, so the
                  text's right edge lands on the midpoint of the sheet -- as on
                  the firm's own invoices, where the right-hand side belongs to
                  the money column.

                  A max-width on the description alone was not enough: the
                  bullet and the right padding sat OUTSIDE the cap, so the row
                  still reached 55% of the page, and an unpriced bullet (with
                  no money column beside it competing for width) ran further
                  still. */}
              <div
                data-line-left=""
                style={{
                  width: "50%",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ minWidth: 15, flexShrink: 0 }}>-</div>
                <div
                  style={{
                    flex: "1 1 auto",
                    minWidth: 0,
                    paddingRight: 20,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                  }}
                >
                  {line.description || ""}
                </div>
              </div>
              {hasAmount ? (
                <MoneyRow
                  {...money$}
                  name="fee"
                  label="Fee"
                  currency={index === firstFeeIndex ? "$" : ""}
                  amount={money(amount)}
                  rule={ruleOnLastFee && index === lastFeeLineIndex ? RULE : "none"}
                  // marginLeft auto, so the fee lands in the SAME column as
                  // Disbursements/PST/GST below it. It used to be 0, which
                  // worked only while the description was flexible and pushed
                  // the fee to the right edge; once the description became a
                  // fixed half-width block the fee butted against it and the
                  // two money columns diverged.
                  style={{ marginLeft: "auto", flexShrink: 0 }}
                />
              ) : null}
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 0,
          lineHeight: TOTALS_LINE_HEIGHT,
        }}
      >
        {hasDiscount ? (
          <MoneyRow
          {...money$}
            name="discount"
            label="Discount"
            amount={bracketed(discount)}
            rule={RULE}
            style={{ marginTop: S.subtotalGap }}
          />
        ) : null}

        <MoneyRow
          name="subtotal"
          amount={money(num(inv.subtotal) - discount)}
          style={hasDiscount ? undefined : { marginTop: S.subtotalGap }}
        />

        <MoneyRow
          {...money$}
          name="disbursements"
          label="Disbursements"
          amount={money(inv.disbursements)}
          style={{ marginTop: S.totalsGroupGap }}
        />

        {showPst ? (
          <MoneyRow
          {...money$}
            name="pst"
            label={pstExempt > 0 ? "* PST" : "PST"}
            // A dash only when nothing is left to tax. A PARTIAL exemption
            // still charges 6% on the remainder, and printing a dash there
            // would under-state the tax the client was actually charged.
            amount={pstAmount > 0 ? money(pstAmount) : "-"}
          />
        ) : null}

        {showGst ? (
          <MoneyRow
          {...money$}
            name="gst"
            label="GST"
            amount={money(gstAmount)}
            rule={RULE}
          />
        ) : null}

        {showHst ? (
          <MoneyRow
          {...money$}
            name="hst"
            label="HST"
            amount={money(hstAmount)}
            rule={RULE}
          />
        ) : null}

        <MoneyRow
          {...money$}
          name="total"
          currency="$"
          amount={money(total)}
          rule={DOUBLE_RULE}
          style={{ marginTop: S.totalsGroupGap }}
        />

        {deposits > 0 ? (
          <MoneyRow
          {...money$}
            name="deposits"
            label="Deposits received"
            currency="$"
            amount={bracketed(deposits)}
            rule={RULE}
            style={{ marginTop: S.totalsGroupGap }}
          />
        ) : null}

        {previousBalance > 0 ? (
          <MoneyRow
          {...money$}
            name="previous-balance"
            label="Previous balance"
            currency="$"
            amount={money(previousBalance)}
            style={{ marginTop: S.totalsGroupGap }}
          />
        ) : null}

        {deposits > 0 || previousBalance > 0 ? (
          <MoneyRow
          {...money$}
            name="balance-due"
            label={deposits > 0 && previousBalance === 0 ? "Balance due" : "Total balance due"}
            currency="$"
            amount={money(balanceDue)}
            rule={DOUBLE_RULE}
            bold
            style={{ marginTop: 10 }}
          />
        ) : null}
      </div>

      {showPst && pstExempt > 0 ? (
        <div data-testid="doc-footnote" style={{ marginTop: 40 }}>
          {`*PST exempt portion $${money(pstExempt)}`}
        </div>
      ) : null}

      {/* Takes up whatever is left of the sheet, so everything below it sits at
          the foot of the page rather than trailing the last total. */}
      <div
        data-testid="doc-spacer"
        style={{ flex: `1 1 ${FOOT_BREATHING_ROOM}px`, minHeight: 0 }}
      />

      <div className="inv-doc-foot">
        <div
          data-testid="doc-gst-registration"
          style={{ textAlign: "center", fontSize: S.fontSize - 1, marginBottom: S.gstGap }}
        >
          GST Registration #730096302
        </div>

        <div data-testid="doc-footer">
          {/* Margin to margin, as on the firm's stationery and the PDF. */}
          <div data-testid="doc-footer-rule" style={{ borderTop: RULE, marginBottom: 12 }} />
          <div
            style={{
              fontSize: 10,
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            1850 MCARA STREET, REGINA, SASKATCHEWAN S4N 6C4 TEL:(306) 721-2476
            EMAIL: LEE@LEEHUMBLECPA.COM
          </div>
        </div>
      </div>
    </div>
  );
}
